import throttle from 'lodash/throttle';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { getCookie, removeCookie } from './cookies';
import { CookieKey } from '@lib/cookie-keys.const';
import { cookieDomain } from './logout-utils';

export const SUPPORT_USER: string = getCookie(CookieKey.SUPPORT_USER);
export const SUPPORT_SESSION: string = getCookie(CookieKey.SUPPORT_SESSION);
export const DEMO_USER: string = getCookie(CookieKey.DEMO_USER);
export const DEMO_SESSION: string = getCookie(CookieKey.DEMO_SESSION);
// On login this cookie value is set after value is read from the cookie here, so we need to update it
let READONLY_USER: string = getCookie(CookieKey.READ_ONLY_MODE);

export function inSupportMode(): boolean {
  return !!SUPPORT_USER;
}

export function inDemoMode(): boolean {
  return !!DEMO_USER;
}

export function inReadOnlyMode(): boolean {
  return !!READONLY_USER;
}

export function isRemoteLoginSession(): boolean {
  return inSupportMode() || inDemoMode() || inReadOnlyMode();
}

export function updateReadOnlyModeCookie(): void {
  READONLY_USER = getCookie(CookieKey.READ_ONLY_MODE);
}

export function displayDemoModeAlert(): void {
  alert('Note: While in Demo Mode, you cannot make requests which modify data and some features are disabled.');
}

export function displaySupportModeAlert(): void {
  alert('Note: While in Support Mode, you cannot make requests which modify data.');
}

export function displayReadOnlyModeAlert(): void {
  alert('Note: While in Readonly, you cannot make requests which modify data.');
}

export function displayRequestDeniedAlert(): void {
  if (inSupportMode()) {
    displaySupportModeAlert();
  } else if (inReadOnlyMode()) {
    displayReadOnlyModeAlert();
  } else if (inDemoMode()) {
    displayDemoModeAlert();
  }
}

@Injectable({
  providedIn: 'root',
})
export class RemoteLoginService implements OnDestroy {
  private readonly EIGHT_HOURS_IN_MS: number = 28800000;
  private readonly FIFTEEN_MINUTES_IN_MS: number = 900000;
  private readonly THROTTLE_TIME_IN_MS: number = 3000;
  private readonly EXPIRY_DATE_EXTENSION: number = this.EIGHT_HOURS_IN_MS;
  private readonly ACTIVITY_CHECK_INTERVAL: number = this.FIFTEEN_MINUTES_IN_MS;
  private readonly REMOTE_LOGIN_EXPIRY: string = 'remote_login_expiry';
  private readonly REMOTE_LOGIN_SESSION: string = 'remote_login_session';
  private readonly cookiePath: string = '/';

  private hasAlertDisplayed: boolean = false;
  private sessionExpiryDate: Date = new Date();
  private validateSessionInterval: NodeJS.Timeout;
  private supportModeCookieKeys: CookieKey[] = [CookieKey.REMOTE_LOGIN_TOKEN, CookieKey.SUPPORT_USER, CookieKey.SUPPORT_SESSION];
  private readOnlyModeCookieKeys: CookieKey[] = [CookieKey.READ_ONLY_MODE];
  private throttledActivityHandler: () => void = throttle(this.handleActivity.bind(this), this.THROTTLE_TIME_IN_MS);

  private get remoteLoginSession(): string {
    if (inDemoMode()) {
      return DEMO_SESSION;
    } else if (inSupportMode()) {
      return SUPPORT_SESSION;
    }
    return null;
  }

  constructor(private router: Router) {}

  public ngOnDestroy(): void {
    window.removeEventListener('mousemove', this.throttledActivityHandler);
  }

  public monitorRemoteLoginSession(): void {
    this.verifyPreviousSession();

    window.addEventListener('mousemove', this.throttledActivityHandler);
    if (this.validateSessionInterval) clearInterval(this.validateSessionInterval);
    this.validateSessionInterval = setInterval(this.validateRemoteLoginSession.bind(this), this.ACTIVITY_CHECK_INTERVAL);
  }

  private verifyPreviousSession(): void {
    if (this.remoteLoginSession === localStorage.getItem(this.REMOTE_LOGIN_SESSION)) {
      const remoteLoginExpiry: string = localStorage.getItem(this.REMOTE_LOGIN_EXPIRY);
      this.sessionExpiryDate = new Date(remoteLoginExpiry);

      if (!remoteLoginExpiry || this.sessionExpiryDate.getTime() <= Date.now()) {
        this.logout();
        this.displayAlert('Your session has expired.');
        return;
      }
    }
    this.extendRemoteLoginSessionExpiry();
  }

  private extendRemoteLoginSessionExpiry(): void {
    this.sessionExpiryDate.setTime(Date.now() + this.EXPIRY_DATE_EXTENSION);
    localStorage.setItem(this.REMOTE_LOGIN_EXPIRY, this.sessionExpiryDate.toUTCString());
    localStorage.setItem(this.REMOTE_LOGIN_SESSION, this.remoteLoginSession);
  }

  private handleActivity(): void {
    this.validateRemoteLoginSession();
    const timeUntilExpiry: number = this.sessionExpiryDate.getTime() - Date.now();
    const extensionInterval: number = this.EXPIRY_DATE_EXTENSION - this.ACTIVITY_CHECK_INTERVAL;
    // Only extend the session after some time has passed to prevent excessive calls
    if (timeUntilExpiry <= extensionInterval) {
      this.extendRemoteLoginSessionExpiry();
    }
  }

  private validateRemoteLoginSession(): void {
    // Log User out if cookie session expiry time has passed
    if (this.sessionExpiryDate.getTime() <= Date.now()) {
      this.logout();
      this.displayAlert('You have been logged out due to inactivity.');
    }
  }

  private displayAlert(message: string): void {
    if (this.hasAlertDisplayed) return;
    alert(message);
    this.hasAlertDisplayed = true;
  }

  private logout(): void {
    localStorage.removeItem(this.REMOTE_LOGIN_EXPIRY);
    localStorage.removeItem(this.REMOTE_LOGIN_SESSION);
    this.router.navigate(['/logout']);
  }
}
